/**
 * Contained "More Responses Needed" empty state for marketer-view destinations
 * (Responses, Benchmarks). Mirrors the dashboard Statement Sentiment empty
 * state (soft amber dashed panel + progress ring to the 10-response minimum +
 * Manage Surveys), so it sits under the page header/controls rather than
 * replacing the whole page like MarketerInsufficientData.
 */

import React from 'react';
import { Button, Progress, Typography } from 'antd';
import { CommentOutlined, RocketOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { useAuth } from '../../../hooks/useAuth';
import { useGetSurveysQuery } from '../../../services/nestjs-base-api';
import { useMarketerScope } from '../scope/MarketerScopeContext';
import { useNavReturn } from '../nav/NavReturnContext';
import { useMarketerRoot } from '../../../hooks/useDashboardConfig';
import { setExploringOnboarding } from '../shell/onboardingExplore';
import { Card } from '../atoms';
import { THEME } from '../config/theme';

const { Title, Text } = Typography;

const MINIMUM_RESPONSES = 10;

interface MarketerResponsesNeededProps {
  totalResponses: number;
  /** Body copy — describes what this destination needs responses for. */
  description: React.ReactNode;
  /** Label for the "← back to" pill dropped when opening Manage Surveys. */
  fromLabel: string;
}

export const MarketerResponsesNeeded: React.FunctionComponent<
  MarketerResponsesNeededProps
> = ({ totalResponses, description, fromLabel }) => {
  const intl = useIntl();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { activeBrand } = useMarketerScope();
  const { navigateWithReturn } = useNavReturn();
  const marketerRoot = useMarketerRoot();

  const percent = Math.min((totalResponses / MINIMUM_RESPONSES) * 100, 100);
  const surveysPath = activeBrand
    ? `${marketerRoot}/${activeBrand.hash}/admin/surveys`
    : '/dashboard/surveys';

  // While onboarding isn't finished AND no survey exists yet, the guided
  // "Finish setup" flow is the clear next step (matches The Briefing + the
  // shell banner) rather than "Manage Surveys". Once a survey exists, setup is
  // effectively done even if the needsOnboarding flag lingers.
  const { data: surveysData } = useGetSurveysQuery(
    {
      jwtToken: user?.jwtToken || '',
      organisationId: user?.organisation?.id || 0,
    },
    { skip: !user?.jwtToken || !user?.organisation?.id },
  );
  const hasActiveSurveys =
    !!surveysData &&
    surveysData.length > 0 &&
    surveysData.some((s: { status?: string }) => s.status === 'active');
  const showFinishSetup = !!user?.needsOnboarding && !hasActiveSurveys;
  const handleFinishSetup = () => {
    setExploringOnboarding(user?.id, false);
    navigate(marketerRoot);
  };

  return (
    <Card style={{ textAlign: 'center', marginTop: 24, padding: '48px 32px' }}>
      <CommentOutlined
        style={{ fontSize: 48, color: THEME.primary, marginBottom: 16 }}
      />
      <Title level={4} style={{ marginBottom: 8, color: '#1f2937' }}>
        {intl.formatMessage({
          id: 'statements.insufficientResponses.title',
          defaultMessage: 'More Responses Needed',
        })}
      </Title>
      <Text
        style={{
          color: '#6b7280',
          fontSize: 15,
          display: 'block',
          marginBottom: 16,
          maxWidth: 520,
          marginLeft: 'auto',
          marginRight: 'auto',
        }}
      >
        {description}
      </Text>
      <div style={{ display: 'flex', justifyContent: 'center', margin: '4px 0 18px' }}>
        <Progress
          type="circle"
          percent={percent}
          size={140}
          strokeColor={THEME.primary}
          trailColor="#F1F5F9"
          format={() => (
            <div style={{ width: 92, margin: '0 auto' }}>
              <div style={{ fontSize: 30, fontWeight: 800, color: THEME.textBase, lineHeight: 1 }}>
                {totalResponses}
              </div>
              <div style={{ fontSize: 11, color: THEME.textSecondary, lineHeight: 1.3, marginTop: 5 }}>
                {intl.formatMessage(
                  {
                    id: 'marketerView.insufficient.ofMin',
                    defaultMessage: 'of {min} responses needed',
                  },
                  { min: MINIMUM_RESPONSES },
                )}
              </div>
            </div>
          )}
        />
      </div>
      {showFinishSetup ? (
        <Button
          type="primary"
          icon={<RocketOutlined />}
          onClick={handleFinishSetup}
          style={{
            background: THEME.primary,
            border: 'none',
          }}
        >
          {intl.formatMessage({
            id: 'marketerView.insufficient.finishSetup',
            defaultMessage: 'Finish setup',
          })}
        </Button>
      ) : (
        <Button
          type="primary"
          onClick={() => navigateWithReturn(surveysPath, fromLabel)}
          style={{
            background: THEME.primary,
            border: 'none',
          }}
        >
          {intl.formatMessage({
            id: 'statements.insufficientResponses.manageSurveys',
            defaultMessage: 'Manage Surveys',
          })}
        </Button>
      )}
    </Card>
  );
};
